import type { User } from 'firebase/auth';
import type { DocumentData, DocumentSnapshot } from 'firebase/firestore';
import { firebaseUserAdapter } from 'adapters';

/**
 * Shape produced by `firebaseUserAdapter` for a signed-in user. `role` is
 * `undefined` when the Firestore doc couldn't be read (see BT-1452) -- the
 * backend re-validates real permissions on login, so this is safe to degrade.
 */
export interface AdaptedFirebaseUser {
  name: string | null;
  email: string | null;
  uid: string;
  role?: string;
}

/** Fetches the Firestore user doc for a given uid. Injectable for tests. */
export type FetchUserDoc = (uid: string) => Promise<DocumentSnapshot<DocumentData>>;

/** Injectable delay, so tests can run with fake timers instead of real waits. */
export type Sleep = (ms: number) => Promise<void>;

export interface ResolveAuthenticatedUserOptions {
  /** Reads the user doc from Firestore. */
  fetchUserDoc: FetchUserDoc;
  /** Total attempts (including the first), default 3. */
  maxAttempts?: number;
  /** Backoff before each retry, in ms. Default [1000, 2000]. */
  retryDelaysMs?: number[];
  /** Called with the last error when every attempt fails. */
  onExhausted?: (error: unknown) => void;
  /** Injectable sleep for backoff. Defaults to a real setTimeout-based wait. */
  sleep?: Sleep;
}

const defaultSleep: Sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Resolves the app user for a signed-in Firebase Auth user, reading the
 * companion Firestore doc with retry + backoff.
 *
 * BT-1452: a corporate network can kill Firestore's WebChannel mid-request
 * ("client is offline" / ERR_CONNECTION_CLOSED). Before this fix that
 * rejection went unhandled inside `onAuthStateChanged`'s callback and the
 * whole app hung on the loading screen forever. This function never throws:
 * after exhausting retries it degrades to a user built only from Firebase
 * Auth data (role stays undefined) and reports the failure via `onExhausted`
 * so it stays observable.
 */
export async function resolveAuthenticatedUser(
  firebaseUser: User,
  options: ResolveAuthenticatedUserOptions,
): Promise<AdaptedFirebaseUser> {
  const {
    fetchUserDoc,
    maxAttempts = 3,
    retryDelaysMs = [1000, 2000],
    onExhausted,
    sleep = defaultSleep,
  } = options;

  let lastError: unknown;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      const snapshot = await fetchUserDoc(firebaseUser.uid);
      return firebaseUserAdapter(firebaseUser, snapshot.data());
    } catch (error) {
      lastError = error;
      const delayMs = retryDelaysMs[attempt];
      const hasMoreAttempts = attempt < maxAttempts - 1;
      if (hasMoreAttempts && delayMs !== undefined) {
        await sleep(delayMs);
      }
    }
  }

  onExhausted?.(lastError);
  return firebaseUserAdapter(firebaseUser, undefined);
}
